import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ImagePlus, ArrowLeftRight, ArrowRight } from "lucide-react";
import { api, API } from "@/lib/api";
import { ImageCompare } from "@/components/ImageCompare";
import { container, sectionPad, label, cardShadow, btnPrimary } from "@/lib/ui";

const img = (id) => `${API}/public-image/${id}`;

export function BeforeAfter({ limit = 6 }) {
  const [items, setItems] = useState(null);

  useEffect(() => {
    api.get("/gallery").then(({ data }) => setItems(data || [])).catch(() => setItems([]));
  }, []);

  const hasItems = items && items.length > 0;
  const shown = hasItems ? items.slice(0, limit) : [];

  return (
    <section className={`${sectionPad} bg-white`} data-testid="before-after">
      <div className={container}>
        <div className="max-w-2xl">
          <span className={label}>Our Work</span>
          <h2 className="mt-3 font-heading text-3xl lg:text-4xl font-bold tracking-tight text-navy">
            {hasItems ? "Real results from real Melbourne homes" : "Real results, coming soon"}
          </h2>
          <p className="mt-4 text-gray-600 leading-relaxed">
            {hasItems
              ? "A look at genuine cleaning projects completed by JK House Cleaning."
              : "We're building a gallery of genuine before & after transformations from real customer cleans."}
          </p>
        </div>

        {items === null ? (
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {[0, 1, 2].map((i) => <div key={i} className="aspect-[4/3] rounded-2xl bg-neutralbg animate-pulse" />)}
          </div>
        ) : hasItems ? (
          <>
            <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {shown.map((it) => (
                <figure key={it.id} className={`rounded-2xl overflow-hidden bg-white border border-gray-100 ${cardShadow}`} data-testid={`gallery-item-${it.id}`}>
                  {it.type === "pair" && it.before_file_id && it.after_file_id ? (
                    <ImageCompare before={img(it.before_file_id)} after={img(it.after_file_id)} alt={it.title || "Cleaning result"} />
                  ) : (
                    <div className="aspect-[4/3] overflow-hidden">
                      <img src={img(it.after_file_id || it.before_file_id)} alt={it.title || "Cleaning result"} className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" loading="lazy" />
                    </div>
                  )}
                  {(it.title || it.caption) && (
                    <figcaption className="p-4">
                      {it.title && <p className="font-heading font-semibold text-navy text-sm">{it.title}</p>}
                      {it.caption && <p className="text-xs text-gray-500 mt-0.5">{it.caption}</p>}
                    </figcaption>
                  )}
                </figure>
              ))}
            </div>
            <div className="mt-10">
              <Link to="/quote" className={btnPrimary} data-testid="gallery-quote">
                Get the same results in your home <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </>
        ) : (
          <>
            <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {[0, 1, 2].map((i) => (
                <div key={i} className="group relative rounded-2xl border-2 border-dashed border-gray-200 bg-neutralbg/60 aspect-[4/3] grid place-items-center transition-colors hover:border-brandgreen/40" data-testid={`before-after-placeholder-${i}`}>
                  <div className="flex items-center gap-2 text-gray-300 absolute top-4 left-4">
                    <ArrowLeftRight className="w-5 h-5" />
                    <span className="text-xs font-semibold uppercase tracking-wider">Before / After</span>
                  </div>
                  <div className="text-center px-6">
                    <span className="grid place-items-center w-14 h-14 mx-auto rounded-2xl bg-white shadow-sm"><ImagePlus className="w-7 h-7 text-brandgreen" /></span>
                    <p className="mt-4 text-sm font-medium text-gray-500">Real customer cleaning projects will be added here.</p>
                  </div>
                </div>
              ))}
            </div>
            <p className="mt-6 text-xs text-gray-400">Administrator can upload before &amp; after images from the dashboard. We never label stock photos as completed jobs.</p>
          </>
        )}
      </div>
    </section>
  );
}
